
/**
 * Interface strings for the 15 storefront languages. Model and colorway
 * names are data and stay unchanged. Translations are bundled locally.
 */
export type Lang = 'en' | 'fr' | 'id' | 'de' | 'es' | 'it' | 'nl' | 'pt-BR' | 'ru' | 'zh-CN' | 'ja' | 'ko' | 'ar' | 'hi' | 'ms';

const STRINGS = {
  "en": {
    "consent_title": "Virtual Try-On",
    "consent_body": "Your camera is used only on this device to place the frame on your face. No images are sent during the try-on. Sending the order file requires your consent.",
    "consent_allow": "Allow Camera Access",
    "consent_cancel": "Cancel",
    "handmade_title": "Size and Colours",
    "material_preview": "Material Preview",
    "sizing_help": "How Does It Work?",
    "sizing_title": "Adjust the Size",
    "sizing_pd": "Total Pupillary Distance for Distance Vision (mm)",
    "sizing_pd_short": "Pupillary Distance",
    "sizing_optional": "Optional",
    "sizing_pd_auto": "Automatically Estimated Pupillary Distance",
    "sizing_apply": "Apply",
    "sizing_reset": "Reset",
    "sizing_pd_note": "Enter your known pupillary distance for distance vision. If you have two values, add the right and left together. Leave it blank for automatic scaling.",
    "sizing_auto": "Automatic Scaling",
    "sizing_handmade": "Frame Width",
    "sizing_handmade_note": "Sizes XS to XL refer to the total width of the frame front. Your pupillary distance is used to scale the preview on your face. The proportions are preserved; final dimensions still need to be confirmed with the workshop.",
    "sizing_calibrating": "Face the camera to scale the preview.",
    "sizing_calibrated": "Preview scaled using your pupillary distance:",
    "loading": "Loading the try-on engine…",
    "hint_no_face": "Position your face in the guide",
    "hint_too_dark": "Find more light",
    "hint_too_far": "Move closer",
    "hint_too_close": "Move back a little",
    "hint_look_at_camera": "Look at the camera",
    "hint_multiple_faces": "One face at a time",
    "hint_hold_still": "Hold still",
    "error_denied": "Camera access was denied. Allow camera access in your browser settings to try on the frames.",
    "error_no_device": "No camera found on this device.",
    "error_insecure": "The try-on requires a secure page (https).",
    "error_in_use": "The camera is being used by another app. Close it and try again.",
    "error_unsupported": "Your browser does not support the try-on. Try Safari (iOS) or Chrome (Android).",
    "error_engine": "The try-on engine could not start.",
    "error_graphics": "Your browser is running low on graphics memory. Close other tabs or refresh the page.",
    "retry": "Try Again",
    "close": "Close",
    "pd_label": "Estimated Pupillary Distance",
    "pd_note": "For guidance only — this is not a medical measurement.",
    "size_suggestion": "Suggested Size"
  },
  "fr": {
    "consent_title": "Essayage virtuel",
    "consent_body": "Votre caméra est utilisée uniquement sur cet appareil pour poser la monture sur votre visage. Aucune image n’est envoyée pendant l’essayage. L’envoi du dossier de commande demande votre accord.",
    "consent_allow": "Autoriser la caméra",
    "consent_cancel": "Annuler",
    "handmade_title": "Taille et couleurs",
    "material_preview": "Aperçu matière",
    "sizing_help": "Comment ça marche ?",
    "sizing_title": "Ajuster la taille",
    "sizing_pd": "Écart pupillaire total de loin (mm)",
    "sizing_pd_short": "Écart pupillaire",
    "sizing_optional": "Facultatif",
    "sizing_pd_auto": "Écart pupillaire estimé automatiquement",
    "sizing_apply": "Appliquer",
    "sizing_reset": "Réinitialiser",
    "sizing_pd_note": "Saisissez votre écart pupillaire de loin connu. Si vous avez deux valeurs, additionnez droite et gauche. Laissez vide pour le réglage automatique.",
    "sizing_auto": "Réglage automatique",
    "sizing_handmade": "Largeur de la monture",
    "sizing_handmade_note": "Les tailles XS à XL correspondent à la largeur totale de l’avant de la monture. Votre écart pupillaire sert à régler l’échelle de l’aperçu sur votre visage. Les proportions sont conservées ; les dimensions finales restent à confirmer avec l’atelier.",
    "sizing_calibrating": "Regardez la caméra de face pour régler l’échelle de l’aperçu.",
    "sizing_calibrated": "Échelle réglée à partir de votre écart pupillaire :",
    "loading": "Chargement du moteur d’essayage…",
    "hint_no_face": "Placez votre visage dans le cadre",
    "hint_too_dark": "Cherchez plus de lumière",
    "hint_too_far": "Rapprochez-vous",
    "hint_too_close": "Reculez un peu",
    "hint_look_at_camera": "Regardez la caméra",
    "hint_multiple_faces": "Un seul visage à la fois",
    "hint_hold_still": "Ne bougez plus",
    "error_denied": "L’accès à la caméra a été refusé. Autorisez la caméra dans les réglages du navigateur pour essayer les montures.",
    "error_no_device": "Aucune caméra trouvée sur cet appareil.",
    "error_insecure": "L’essayage nécessite une page sécurisée (https).",
    "error_in_use": "La caméra est utilisée par une autre application. Fermez-la puis réessayez.",
    "error_unsupported": "Votre navigateur ne prend pas en charge l’essayage. Essayez Safari (iOS) ou Chrome (Android).",
    "error_engine": "Le moteur d’essayage n’a pas pu démarrer.",
    "error_graphics": "Votre navigateur manque de mémoire graphique. Fermez d’autres onglets ou actualisez la page.",
    "retry": "Réessayer",
    "close": "Fermer",
    "pd_label": "Écart pupillaire estimé",
    "pd_note": "Indicatif uniquement — ce n’est pas une mesure médicale.",
    "size_suggestion": "Taille suggérée"
  },
  "ar": {
    "consent_title": "تجربة افتراضية",
    "consent_body": "تُستخدم الكاميرا على هذا الجهاز فقط لوضع الإطار على وجهكم. لا تُرسل صور أثناء التجربة. يتطلب إرسال ملف الطلب موافقتكم.",
    "consent_allow": "السماح باستخدام الكاميرا",
    "consent_cancel": "إلغاء",
    "handmade_title": "المقاس والألوان",
    "material_preview": "معاينة المادة",
    "sizing_help": "كيف تعمل؟",
    "sizing_title": "تعديل المقاس",
    "sizing_pd": "المسافة الكلية بين الحدقتين للرؤية البعيدة (مم)",
    "sizing_pd_short": "المسافة بين الحدقتين",
    "sizing_optional": "اختياري",
    "sizing_pd_auto": "تقدير آلي للمسافة بين الحدقتين",
    "sizing_apply": "تطبيق",
    "sizing_reset": "إعادة ضبط",
    "sizing_pd_note": "أدخلوا المسافة المعروفة بين حدقتيكم للرؤية البعيدة. إذا كانت لديكم قيمتان، اجمعوا اليمنى واليسرى. اتركوا الحقل فارغًا لضبط المقياس تلقائيًا.",
    "sizing_auto": "ضبط المقياس تلقائيًا",
    "sizing_handmade": "عرض الإطار",
    "sizing_handmade_note": "تشير المقاسات من XS إلى XL إلى العرض الكلي لواجهة الإطار. تُستخدم المسافة بين الحدقتين لضبط مقياس المعاينة على وجهكم. تبقى النسب محفوظة؛ ويجب تأكيد الأبعاد النهائية مع الورشة.",
    "sizing_calibrating": "واجهوا الكاميرا لضبط مقياس المعاينة.",
    "sizing_calibrated": "ضُبطت المعاينة باستخدام المسافة بين حدقتيكم:",
    "loading": "جارٍ تحميل محرك التجربة…",
    "hint_no_face": "ضعوا الوجه داخل الدليل",
    "hint_too_dark": "انتقلوا إلى إضاءة أقوى",
    "hint_too_far": "اقتربوا",
    "hint_too_close": "ابتعدوا قليلًا",
    "hint_look_at_camera": "انظروا إلى الكاميرا",
    "hint_multiple_faces": "وجه واحد في كل مرة",
    "hint_hold_still": "ابقوا ثابتين",
    "error_denied": "رُفض الوصول إلى الكاميرا. اسمحوا باستخدامها في إعدادات المتصفح لتجربة الإطارات.",
    "error_no_device": "لم نعثر على كاميرا في هذا الجهاز.",
    "error_insecure": "تتطلب التجربة صفحة آمنة (https).",
    "error_in_use": "يستخدم تطبيق آخر الكاميرا. أغلقوه وحاولوا مجددًا.",
    "error_unsupported": "لا يدعم المتصفح التجربة. جربوا Safari (iOS) أو Chrome (Android).",
    "error_engine": "تعذر تشغيل محرك التجربة.",
    "error_graphics": "ذاكرة الرسوم المتاحة للمتصفح منخفضة. أغلقوا علامات تبويب أخرى أو حدّثوا الصفحة.",
    "retry": "المحاولة مجددًا",
    "close": "إغلاق",
    "pd_label": "المسافة المقدرة بين الحدقتين",
    "pd_note": "للاسترشاد فقط — ليست قياسًا طبيًا.",
    "size_suggestion": "المقاس المقترح"
  },
  "de": {
    "consent_title": "Virtuelle Anprobe",
    "consent_body": "Deine Kamera wird nur auf diesem Gerät verwendet, um die Fassung auf deinem Gesicht anzuzeigen. Während der Anprobe werden keine Bilder übertragen. Für das Senden der Bestellunterlagen ist deine Zustimmung erforderlich.",
    "consent_allow": "Kamera erlauben",
    "consent_cancel": "Abbrechen",
    "handmade_title": "Größe und Farben",
    "material_preview": "Materialvorschau",
    "sizing_help": "Wie funktioniert es?",
    "sizing_title": "Größe anpassen",
    "sizing_pd": "Gesamter Pupillenabstand für die Ferne (mm)",
    "sizing_pd_short": "Pupillenabstand",
    "sizing_optional": "Optional",
    "sizing_pd_auto": "Automatisch geschätzter Pupillenabstand",
    "sizing_apply": "Anwenden",
    "sizing_reset": "Zurücksetzen",
    "sizing_pd_note": "Gib deinen bekannten Pupillenabstand für die Ferne ein. Wenn du zwei Werte hast, addiere rechts und links. Lass das Feld für die automatische Einstellung leer.",
    "sizing_auto": "Automatische Einstellung",
    "sizing_handmade": "Fassungsbreite",
    "sizing_handmade_note": "Die Größen XS bis XL entsprechen der Gesamtbreite der Fassungsfront. Dein Pupillenabstand dient dazu, den Maßstab der Vorschau auf deinem Gesicht einzustellen. Die Proportionen bleiben erhalten; die endgültigen Maße müssen noch mit dem Atelier abgestimmt werden.",
    "sizing_calibrating": "Schau gerade in die Kamera, um den Maßstab der Vorschau einzustellen.",
    "sizing_calibrated": "Maßstab anhand deines Pupillenabstands eingestellt:",
    "loading": "Virtuelle Anprobe wird geladen…",
    "hint_no_face": "Halte dein Gesicht in den Rahmen",
    "hint_too_dark": "Such dir einen helleren Platz",
    "hint_too_far": "Komm näher",
    "hint_too_close": "Geh etwas zurück",
    "hint_look_at_camera": "Schau in die Kamera",
    "hint_multiple_faces": "Nur ein Gesicht auf einmal",
    "hint_hold_still": "Halte still",
    "error_denied": "Der Kamerazugriff wurde abgelehnt. Erlaube die Kamera in den Browsereinstellungen, um die Fassungen anzuprobieren.",
    "error_no_device": "Auf diesem Gerät wurde keine Kamera gefunden.",
    "error_insecure": "Die Anprobe benötigt eine sichere Seite (https).",
    "error_in_use": "Die Kamera wird von einer anderen Anwendung verwendet. Schließe diese und versuche es erneut.",
    "error_unsupported": "Dein Browser unterstützt die Anprobe nicht. Versuche es mit Safari (iOS) oder Chrome (Android).",
    "error_engine": "Die virtuelle Anprobe konnte nicht gestartet werden.",
    "error_graphics": "Deinem Browser steht nicht genügend Grafikspeicher zur Verfügung. Schließe andere Tabs oder lade die Seite neu.",
    "retry": "Erneut versuchen",
    "close": "Schließen",
    "pd_label": "Geschätzter Pupillenabstand",
    "pd_note": "Nur zur Orientierung — keine medizinische Messung.",
    "size_suggestion": "Vorgeschlagene Größe"
  },
  "es": {
    "consent_title": "Probador virtual",
    "consent_body": "La cámara se utiliza solo en este dispositivo para colocar la montura sobre tu rostro. No se envía ninguna imagen durante la prueba. Para enviar los datos del pedido se necesita tu consentimiento.",
    "consent_allow": "Permitir la cámara",
    "consent_cancel": "Cancelar",
    "handmade_title": "Talla y colores",
    "material_preview": "Vista previa del material",
    "sizing_help": "¿Cómo funciona?",
    "sizing_title": "Ajustar la talla",
    "sizing_pd": "Distancia interpupilar total para visión de lejos (mm)",
    "sizing_pd_short": "Distancia interpupilar",
    "sizing_optional": "Opcional",
    "sizing_pd_auto": "Distancia interpupilar estimada automáticamente",
    "sizing_apply": "Aplicar",
    "sizing_reset": "Restablecer",
    "sizing_pd_note": "Introduce tu distancia interpupilar para visión de lejos si la conoces. Si tienes dos valores, suma el derecho y el izquierdo. Déjalo en blanco para usar el ajuste automático.",
    "sizing_auto": "Ajuste automático",
    "sizing_handmade": "Ancho de la montura",
    "sizing_handmade_note": "Las tallas XS a XL corresponden al ancho total del frontal de la montura. Tu distancia interpupilar sirve para ajustar la escala de la vista previa sobre tu rostro. Se mantienen las proporciones; las medidas finales deben confirmarse con el taller.",
    "sizing_calibrating": "Mira de frente a la cámara para ajustar la escala de la vista previa.",
    "sizing_calibrated": "Escala ajustada a partir de tu distancia interpupilar:",
    "loading": "Cargando el motor del probador…",
    "hint_no_face": "Coloca tu rostro dentro del recuadro",
    "hint_too_dark": "Busca un lugar con más luz",
    "hint_too_far": "Acércate",
    "hint_too_close": "Aléjate un poco",
    "hint_look_at_camera": "Mira a la cámara",
    "hint_multiple_faces": "Solo un rostro a la vez",
    "hint_hold_still": "Quédate sin moverte",
    "error_denied": "Se ha denegado el acceso a la cámara. Permítelo en los ajustes del navegador para probarte las monturas.",
    "error_no_device": "No se ha encontrado ninguna cámara en este dispositivo.",
    "error_insecure": "El probador necesita una página segura (https).",
    "error_in_use": "Otra aplicación está usando la cámara. Ciérrala y vuelve a intentarlo.",
    "error_unsupported": "Tu navegador no es compatible con el probador. Prueba con Safari (iOS) o Chrome (Android).",
    "error_engine": "No se ha podido iniciar el motor del probador.",
    "error_graphics": "Tu navegador no tiene suficiente memoria gráfica. Cierra otras pestañas o actualiza la página.",
    "retry": "Volver a intentar",
    "close": "Cerrar",
    "pd_label": "Distancia interpupilar estimada",
    "pd_note": "Solo orientativa: no es una medición médica.",
    "size_suggestion": "Talla sugerida"
  },
  "hi": {
    "consent_title": "वर्चुअल ट्राई-ऑन",
    "consent_body": "चेहरे पर फ्रेम दिखाने के लिए कैमरे का उपयोग केवल इसी उपकरण पर होता है। आज़माने के दौरान कोई तस्वीर भेजी नहीं जाती। ऑर्डर फ़ाइल भेजने के लिए आपकी सहमति आवश्यक है।",
    "consent_allow": "कैमरे की अनुमति दें",
    "consent_cancel": "रद्द करें",
    "handmade_title": "आकार और रंग",
    "material_preview": "सामग्री की झलक",
    "sizing_help": "यह कैसे काम करता है?",
    "sizing_title": "आकार ठीक करें",
    "sizing_pd": "दूर की नज़र के लिए पुतलियों के बीच कुल दूरी (mm)",
    "sizing_pd_short": "पुतलियों के बीच की दूरी",
    "sizing_optional": "वैकल्पिक",
    "sizing_pd_auto": "अपने आप अनुमानित पुतलियों की दूरी",
    "sizing_apply": "लागू करें",
    "sizing_reset": "रीसेट करें",
    "sizing_pd_note": "दूर की नज़र के लिए पुतलियों के बीच की अपनी ज्ञात दूरी दर्ज करें। दो मान हों तो दाएँ और बाएँ को जोड़ दें। अपने आप आकार मिलाने के लिए इसे खाली छोड़ दें।",
    "sizing_auto": "अपने आप आकार मिलाना",
    "sizing_handmade": "फ्रेम की चौड़ाई",
    "sizing_handmade_note": "XS से XL आकार फ्रेम के सामने के हिस्से की कुल चौड़ाई बताते हैं। पुतलियों के बीच की दूरी से आपके चेहरे पर पूर्वावलोकन का आकार मिलाया जाता है। अनुपात बने रहते हैं; अंतिम माप की पुष्टि वर्कशॉप से करना अभी आवश्यक है।",
    "sizing_calibrating": "पूर्वावलोकन का आकार मिलाने के लिए कैमरे की ओर चेहरा करें।",
    "sizing_calibrated": "पुतलियों की दूरी के अनुसार पूर्वावलोकन का आकार मिला दिया गया है:",
    "loading": "ट्राई-ऑन इंजन लोड हो रहा है…",
    "hint_no_face": "चेहरा दिए गए घेरे में रखें",
    "hint_too_dark": "अधिक रोशनी में जाएँ",
    "hint_too_far": "थोड़ा पास आएँ",
    "hint_too_close": "थोड़ा पीछे जाएँ",
    "hint_look_at_camera": "कैमरे की ओर देखें",
    "hint_multiple_faces": "एक समय में एक चेहरा",
    "hint_hold_still": "स्थिर रहें",
    "error_denied": "कैमरे की अनुमति नहीं मिली। फ्रेम आज़माने के लिए अपने ब्राउज़र की सेटिंग में कैमरे की अनुमति दें।",
    "error_no_device": "इस उपकरण पर कैमरा नहीं मिला।",
    "error_insecure": "ट्राई-ऑन के लिए सुरक्षित पेज (https) आवश्यक है।",
    "error_in_use": "कैमरा दूसरी ऐप में उपयोग हो रहा है। उसे बंद करके फिर कोशिश करें।",
    "error_unsupported": "आपका ब्राउज़र ट्राई-ऑन को समर्थित नहीं करता। Safari (iOS) या Chrome (Android) आज़माएँ।",
    "error_engine": "ट्राई-ऑन इंजन शुरू नहीं हो सका।",
    "error_graphics": "आपके ब्राउज़र की ग्राफ़िक्स मेमोरी कम है। दूसरे टैब बंद करें या पेज रिफ़्रेश करें।",
    "retry": "फिर कोशिश करें",
    "close": "बंद करें",
    "pd_label": "पुतलियों की अनुमानित दूरी",
    "pd_note": "केवल मार्गदर्शन के लिए — यह चिकित्सकीय माप नहीं है।",
    "size_suggestion": "सुझाया गया आकार"
  },
  "id": {
    "consent_title": "Coba kacamata secara virtual",
    "consent_body": "Kamera hanya digunakan pada perangkat ini untuk menempatkan frame pada wajah Anda. Tidak ada gambar yang dikirim selama mencoba. Pengiriman berkas pesanan memerlukan persetujuan Anda.",
    "consent_allow": "Izinkan kamera",
    "consent_cancel": "Batal",
    "handmade_title": "Ukuran dan warna",
    "material_preview": "Pratinjau bahan",
    "sizing_help": "Bagaimana cara kerjanya?",
    "sizing_title": "Sesuaikan ukuran",
    "sizing_pd": "Jarak total antarpupil untuk penglihatan jauh (mm)",
    "sizing_pd_short": "Jarak antarpupil",
    "sizing_optional": "Opsional",
    "sizing_pd_auto": "Perkiraan jarak antarpupil otomatis",
    "sizing_apply": "Terapkan",
    "sizing_reset": "Atur ulang",
    "sizing_pd_note": "Masukkan jarak antarpupil untuk penglihatan jauh yang sudah Anda ketahui. Jika ada dua nilai, jumlahkan kanan dan kiri. Biarkan kosong untuk pengaturan otomatis.",
    "sizing_auto": "Pengaturan otomatis",
    "sizing_handmade": "Lebar frame",
    "sizing_handmade_note": "Ukuran XS hingga XL menunjukkan lebar total bagian depan frame. Jarak antarpupil digunakan untuk menyesuaikan skala pratinjau pada wajah Anda. Proporsi tetap dipertahankan; ukuran akhir masih perlu dikonfirmasi dengan atelier.",
    "sizing_calibrating": "Hadapkan wajah ke kamera untuk menyesuaikan skala pratinjau.",
    "sizing_calibrated": "Skala disesuaikan berdasarkan jarak antarpupil Anda:",
    "loading": "Memuat sistem coba virtual…",
    "hint_no_face": "Posisikan wajah Anda di dalam bingkai",
    "hint_too_dark": "Cari tempat yang lebih terang",
    "hint_too_far": "Mendekatlah",
    "hint_too_close": "Mundurlah sedikit",
    "hint_look_at_camera": "Lihat ke kamera",
    "hint_multiple_faces": "Satu wajah saja pada satu waktu",
    "hint_hold_still": "Tahan posisi",
    "error_denied": "Akses kamera ditolak. Izinkan kamera di pengaturan browser untuk mencoba frame.",
    "error_no_device": "Tidak ditemukan kamera pada perangkat ini.",
    "error_insecure": "Fitur coba virtual memerlukan halaman yang aman (https).",
    "error_in_use": "Kamera sedang digunakan aplikasi lain. Tutup aplikasi tersebut lalu coba lagi.",
    "error_unsupported": "Browser Anda tidak mendukung fitur coba virtual. Coba Safari (iOS) atau Chrome (Android).",
    "error_engine": "Sistem coba virtual tidak dapat dimulai.",
    "error_graphics": "Memori grafis browser Anda tidak mencukupi. Tutup tab lain atau muat ulang halaman.",
    "retry": "Coba lagi",
    "close": "Tutup",
    "pd_label": "Perkiraan jarak antarpupil",
    "pd_note": "Hanya sebagai panduan — bukan pengukuran medis.",
    "size_suggestion": "Ukuran yang disarankan"
  },
  "it": {
    "consent_title": "Prova virtuale",
    "consent_body": "La fotocamera viene utilizzata solo su questo dispositivo per posizionare la montatura sul viso. Durante la prova non viene inviata alcuna immagine. L’invio del dossier d’ordine richiede il tuo consenso.",
    "consent_allow": "Consenti l’accesso alla fotocamera",
    "consent_cancel": "Annulla",
    "handmade_title": "Misura e colori",
    "material_preview": "Anteprima del materiale",
    "sizing_help": "Come funziona?",
    "sizing_title": "Regola la misura",
    "sizing_pd": "Distanza interpupillare totale da lontano (mm)",
    "sizing_pd_short": "Distanza interpupillare",
    "sizing_optional": "Facoltativo",
    "sizing_pd_auto": "Distanza interpupillare stimata automaticamente",
    "sizing_apply": "Applica",
    "sizing_reset": "Reimposta",
    "sizing_pd_note": "Inserisci la distanza interpupillare da lontano che conosci. Se hai due valori, somma destro e sinistro. Lascia vuoto per la regolazione automatica.",
    "sizing_auto": "Regolazione automatica",
    "sizing_handmade": "Larghezza della montatura",
    "sizing_handmade_note": "Le misure da XS a XL corrispondono alla larghezza totale del frontale della montatura. La distanza interpupillare serve a regolare la scala dell’anteprima sul viso. Le proporzioni vengono mantenute; le dimensioni finali restano da confermare con il laboratorio.",
    "sizing_calibrating": "Guarda la fotocamera frontalmente per regolare la scala dell’anteprima.",
    "sizing_calibrated": "Scala regolata in base alla distanza interpupillare:",
    "loading": "Caricamento del sistema di prova…",
    "hint_no_face": "Posiziona il viso nel riquadro",
    "hint_too_dark": "Cerca più luce",
    "hint_too_far": "Avvicinati",
    "hint_too_close": "Allontanati un po’",
    "hint_look_at_camera": "Guarda la fotocamera",
    "hint_multiple_faces": "Un solo viso alla volta",
    "hint_hold_still": "Non muoverti",
    "error_denied": "L’accesso alla fotocamera è stato negato. Autorizzala nelle impostazioni del browser per provare le montature.",
    "error_no_device": "Nessuna fotocamera trovata su questo dispositivo.",
    "error_insecure": "La prova richiede una pagina sicura (https).",
    "error_in_use": "La fotocamera è utilizzata da un’altra applicazione. Chiudila e riprova.",
    "error_unsupported": "Il browser non supporta la prova. Prova Safari (iOS) o Chrome (Android).",
    "error_engine": "Impossibile avviare il sistema di prova.",
    "error_graphics": "Il browser non ha memoria grafica sufficiente. Chiudi altre schede o aggiorna la pagina.",
    "retry": "Riprova",
    "close": "Chiudi",
    "pd_label": "Distanza interpupillare stimata",
    "pd_note": "Solo indicativa — non è una misurazione medica.",
    "size_suggestion": "Misura suggerita"
  },
  "ja": {
    "consent_title": "バーチャル試着",
    "consent_body": "カメラは、この端末上でフレームを顔に重ねるためにのみ使用します。試着中に画像が送信されることはありません。注文用データの送信には、お客様の同意が必要です。",
    "consent_allow": "カメラを許可する",
    "consent_cancel": "キャンセル",
    "handmade_title": "サイズと色",
    "material_preview": "素材のプレビュー",
    "sizing_help": "使い方",
    "sizing_title": "サイズを調整する",
    "sizing_pd": "遠方視の両眼瞳孔間距離（mm）",
    "sizing_pd_short": "瞳孔間距離",
    "sizing_optional": "任意",
    "sizing_pd_auto": "瞳孔間距離を自動推定",
    "sizing_apply": "適用",
    "sizing_reset": "リセット",
    "sizing_pd_note": "分かっている遠方視の瞳孔間距離を入力してください。左右それぞれの値がある場合は、足し合わせてください。空欄の場合は自動調整します。",
    "sizing_auto": "自動調整",
    "sizing_handmade": "フレームの幅",
    "sizing_handmade_note": "XS〜XL はフレームのフロント全体の幅を表します。瞳孔間距離を使い、顔に重ねるプレビューの縮尺を調整します。比率は保たれますが、最終的な寸法は工房との確認が必要です。",
    "sizing_calibrating": "プレビューの縮尺を調整するため、カメラを正面から見てください。",
    "sizing_calibrated": "瞳孔間距離に基づいて縮尺を調整しました：",
    "loading": "試着エンジンを読み込み中…",
    "hint_no_face": "顔を枠の中に入れてください",
    "hint_too_dark": "もう少し明るい場所へ移動してください",
    "hint_too_far": "もう少し近づいてください",
    "hint_too_close": "少し離れてください",
    "hint_look_at_camera": "カメラを見てください",
    "hint_multiple_faces": "顔は一度に1人だけ映してください",
    "hint_hold_still": "そのまま動かずにお待ちください",
    "error_denied": "カメラへのアクセスが拒否されました。フレームを試すには、ブラウザーの設定でカメラを許可してください。",
    "error_no_device": "この端末にカメラが見つかりません。",
    "error_insecure": "試着には安全なページ（https）が必要です。",
    "error_in_use": "別のアプリがカメラを使用しています。そのアプリを閉じてから、もう一度お試しください。",
    "error_unsupported": "このブラウザーは試着に対応していません。Safari（iOS）または Chrome（Android）をお試しください。",
    "error_engine": "試着エンジンを起動できませんでした。",
    "error_graphics": "ブラウザーのグラフィックスメモリーが不足しています。他のタブを閉じるか、ページを再読み込みしてください。",
    "retry": "もう一度試す",
    "close": "閉じる",
    "pd_label": "推定瞳孔間距離",
    "pd_note": "参考値であり、医療上の測定ではありません。",
    "size_suggestion": "おすすめのサイズ"
  },
  "ko": {
    "consent_title": "가상 착용",
    "consent_body": "카메라는 얼굴에 프레임을 표시하기 위해 이 기기에서만 사용돼요. 착용 중에는 이미지를 전송하지 않아요. 주문 자료 전송에는 별도의 동의가 필요해요.",
    "consent_allow": "카메라 허용",
    "consent_cancel": "취소",
    "handmade_title": "크기와 색상",
    "material_preview": "소재 미리보기",
    "sizing_help": "어떻게 사용하나요?",
    "sizing_title": "크기 조정",
    "sizing_pd": "원거리 양안 동공간거리(mm)",
    "sizing_pd_short": "동공간거리",
    "sizing_optional": "선택사항",
    "sizing_pd_auto": "자동 추정 동공간거리",
    "sizing_apply": "적용",
    "sizing_reset": "초기화",
    "sizing_pd_note": "알고 있는 원거리 동공간거리를 입력해 주세요. 값이 두 개라면 오른쪽과 왼쪽을 더해 주세요. 비워두면 자동으로 조정돼요.",
    "sizing_auto": "자동 조정",
    "sizing_handmade": "프레임 너비",
    "sizing_handmade_note": "XS부터 XL까지의 크기는 프레임 전면부의 전체 너비를 뜻해요. 동공간거리는 얼굴 위 미리보기의 비율을 맞추는 데 사용해요. 형태의 비율은 유지되며 최종 치수는 공방과 확인해야 해요.",
    "sizing_calibrating": "미리보기의 크기 비율을 맞추려면 카메라를 정면으로 바라봐 주세요.",
    "sizing_calibrated": "동공간거리를 바탕으로 조정한 비율:",
    "loading": "가상 착용 엔진을 불러오는 중…",
    "hint_no_face": "화면 안에 얼굴을 맞춰주세요",
    "hint_too_dark": "더 밝은 곳으로 이동해 주세요",
    "hint_too_far": "가까이 와주세요",
    "hint_too_close": "조금 뒤로 이동해 주세요",
    "hint_look_at_camera": "카메라를 바라봐 주세요",
    "hint_multiple_faces": "한 번에 한 사람의 얼굴만 보여주세요",
    "hint_hold_still": "잠시 움직이지 말아주세요",
    "error_denied": "카메라 접근이 거부되었어요. 프레임을 착용해 보려면 브라우저 설정에서 카메라를 허용해 주세요.",
    "error_no_device": "이 기기에서 카메라를 찾을 수 없어요.",
    "error_insecure": "가상 착용에는 보안 페이지(https)가 필요해요.",
    "error_in_use": "다른 앱에서 카메라를 사용하고 있어요. 해당 앱을 닫고 다시 시도해 주세요.",
    "error_unsupported": "이 브라우저는 가상 착용을 지원하지 않아요. Safari(iOS) 또는 Chrome(Android)을 사용해 보세요.",
    "error_engine": "가상 착용 엔진을 시작하지 못했어요.",
    "error_graphics": "브라우저의 그래픽 메모리가 부족해요. 다른 탭을 닫거나 페이지를 새로고침해 주세요.",
    "retry": "다시 시도",
    "close": "닫기",
    "pd_label": "추정 동공간거리",
    "pd_note": "참고용이며 의료 목적의 측정값이 아니에요.",
    "size_suggestion": "추천 크기"
  },
  "ms": {
    "consent_title": "Cuba cermin mata secara maya",
    "consent_body": "Kamera hanya digunakan pada peranti ini untuk meletakkan bingkai pada wajah anda. Tidak ada gambar yang dihantar selama mencuba. Penghantaran fail pesanan memerlukan persetujuan anda.",
    "consent_allow": "Benarkan kamera",
    "consent_cancel": "Batal",
    "handmade_title": "Saiz dan warna",
    "material_preview": "Pratonton bahan",
    "sizing_help": "Bagaimana ia berfungsi?",
    "sizing_title": "Sesuaikan saiz",
    "sizing_pd": "Jumlah jarak antara pupil untuk penglihatan jauh (mm)",
    "sizing_pd_short": "Jarak antara pupil",
    "sizing_optional": "Pilihan",
    "sizing_pd_auto": "Anggaran automatik jarak antara pupil",
    "sizing_apply": "Gunakan",
    "sizing_reset": "Tetapkan semula",
    "sizing_pd_note": "Masukkan jarak antara pupil untuk penglihatan jauh yang sudah anda ketahui. Jika ada dua nilai, jumlahkan kanan dan kiri. Biarkan kosong untuk tetapan automatik.",
    "sizing_auto": "Tetapan automatik",
    "sizing_handmade": "Lebar bingkai",
    "sizing_handmade_note": "Saiz XS hingga XL menunjukkan lebar keseluruhan bahagian depan bingkai. Jarak antara pupil digunakan untuk menyesuaikan skala pratonton pada wajah anda. Perkadaran tetap dipertahankan; saiz akhir masih perlu disahkan dengan bengkel.",
    "sizing_calibrating": "Hadapkan wajah ke kamera untuk menyesuaikan skala pratonton.",
    "sizing_calibrated": "Skala disesuaikan berdasarkan jarak antara pupil anda:",
    "loading": "Memuatkan sistem cuba maya…",
    "hint_no_face": "Letakkan wajah anda di dalam bingkai",
    "hint_too_dark": "Cari tempat yang lebih terang",
    "hint_too_far": "Dekatkan wajah",
    "hint_too_close": "Undur sedikit",
    "hint_look_at_camera": "Lihat ke kamera",
    "hint_multiple_faces": "Satu wajah sahaja pada satu masa",
    "hint_hold_still": "Kekalkan kedudukan wajah",
    "error_denied": "Akses kamera ditolak. Benarkan kamera di tetapan pelayar untuk mencuba bingkai.",
    "error_no_device": "Tiada kamera dikesan pada peranti ini.",
    "error_insecure": "Ciri cuba maya memerlukan halaman yang selamat (https).",
    "error_in_use": "Kamera sedang digunakan aplikasi lain. Tutup aplikasi tersebut kemudian cuba lagi.",
    "error_unsupported": "Pelayar anda tidak menyokong ciri cuba maya. Cuba Safari (iOS) atau Chrome (Android).",
    "error_engine": "Sistem cubaan maya tidak dapat dimulakan.",
    "error_graphics": "Memori grafik pelayar anda tidak mencukupi. Tutup tab lain atau muat semula halaman.",
    "retry": "Cuba lagi",
    "close": "Tutup",
    "pd_label": "Anggaran jarak antara pupil",
    "pd_note": "Hanya sebagai panduan — bukan pengukuran perubatan.",
    "size_suggestion": "Saiz yang disyorkan"
  },
  "nl": {
    "consent_title": "Virtueel passen",
    "consent_body": "Je camera wordt alleen op dit apparaat gebruikt om het montuur op je gezicht te plaatsen. Tijdens het passen worden geen beelden verzonden. Het verzenden van het besteldossier vraagt om je toestemming.",
    "consent_allow": "Camera toestaan",
    "consent_cancel": "Annuleren",
    "handmade_title": "Maat en kleuren",
    "material_preview": "Materiaalvoorbeeld",
    "sizing_help": "Hoe werkt het?",
    "sizing_title": "Maat aanpassen",
    "sizing_pd": "Totale pupilafstand voor veraf (mm)",
    "sizing_pd_short": "Pupilafstand",
    "sizing_optional": "Optioneel",
    "sizing_pd_auto": "Automatisch geschatte pupilafstand",
    "sizing_apply": "Toepassen",
    "sizing_reset": "Herstellen",
    "sizing_pd_note": "Vul je bekende pupilafstand voor veraf in. Heb je twee waarden, tel rechts en links dan op. Laat leeg voor automatische instelling.",
    "sizing_auto": "Automatische instelling",
    "sizing_handmade": "Montuurbreedte",
    "sizing_handmade_note": "Maten XS tot XL komen overeen met de totale breedte van de voorkant van het montuur. Je pupilafstand wordt gebruikt om de schaal van het voorbeeld op je gezicht in te stellen. De verhoudingen blijven behouden; de definitieve afmetingen moeten nog met het atelier worden bevestigd.",
    "sizing_calibrating": "Kijk recht in de camera om de schaal van het voorbeeld in te stellen.",
    "sizing_calibrated": "Schaal ingesteld op basis van je pupilafstand:",
    "loading": "Virtuele pasfunctie laden…",
    "hint_no_face": "Plaats je gezicht in het kader",
    "hint_too_dark": "Zoek meer licht",
    "hint_too_far": "Kom dichterbij",
    "hint_too_close": "Ga iets achteruit",
    "hint_look_at_camera": "Kijk in de camera",
    "hint_multiple_faces": "Eén gezicht tegelijk",
    "hint_hold_still": "Blijf stil",
    "error_denied": "Cameratoegang is geweigerd. Sta de camera toe in je browserinstellingen om monturen te passen.",
    "error_no_device": "Geen camera gevonden op dit apparaat.",
    "error_insecure": "Virtueel passen vereist een beveiligde pagina (https).",
    "error_in_use": "Een andere app gebruikt de camera. Sluit die en probeer opnieuw.",
    "error_unsupported": "Je browser ondersteunt virtueel passen niet. Probeer Safari (iOS) of Chrome (Android).",
    "error_engine": "De virtuele pasfunctie kon niet starten.",
    "error_graphics": "Je browser heeft te weinig grafisch geheugen. Sluit andere tabbladen of vernieuw de pagina.",
    "retry": "Opnieuw proberen",
    "close": "Sluiten",
    "pd_label": "Geschatte pupilafstand",
    "pd_note": "Alleen ter indicatie — dit is geen medische meting.",
    "size_suggestion": "Voorgestelde maat"
  },
  "pt-BR": {
    "consent_title": "Provador virtual",
    "consent_body": "Sua câmera é usada apenas neste dispositivo para posicionar a armação no rosto. Nenhuma imagem é enviada durante a prova. O envio dos dados do pedido exige seu consentimento.",
    "consent_allow": "Permitir câmera",
    "consent_cancel": "Cancelar",
    "handmade_title": "Tamanho e cores",
    "material_preview": "Prévia do material",
    "sizing_help": "Como funciona?",
    "sizing_title": "Ajustar tamanho",
    "sizing_pd": "Distância pupilar total para longe (mm)",
    "sizing_pd_short": "Distância pupilar",
    "sizing_optional": "Opcional",
    "sizing_pd_auto": "Distância pupilar estimada automaticamente",
    "sizing_apply": "Aplicar",
    "sizing_reset": "Redefinir",
    "sizing_pd_note": "Informe sua distância pupilar para longe, se conhecida. Se tiver dois valores, some o direito e o esquerdo. Deixe em branco para o ajuste automático.",
    "sizing_auto": "Ajuste automático",
    "sizing_handmade": "Largura da armação",
    "sizing_handmade_note": "Os tamanhos XS a XL correspondem à largura total da frente da armação. Sua distância pupilar serve para ajustar a escala da prévia no rosto. As proporções são mantidas; as dimensões finais ainda precisam ser confirmadas com o ateliê.",
    "sizing_calibrating": "Olhe de frente para a câmera para ajustar a escala da prévia.",
    "sizing_calibrated": "Escala ajustada a partir da sua distância pupilar:",
    "loading": "Carregando o sistema de prova…",
    "hint_no_face": "Posicione o rosto dentro do quadro",
    "hint_too_dark": "Procure mais luz",
    "hint_too_far": "Aproxime-se",
    "hint_too_close": "Afaste-se um pouco",
    "hint_look_at_camera": "Olhe para a câmera",
    "hint_multiple_faces": "Apenas um rosto por vez",
    "hint_hold_still": "Não se mova",
    "error_denied": "O acesso à câmera foi negado. Permita o acesso nas configurações do navegador para experimentar as armações.",
    "error_no_device": "Nenhuma câmera encontrada neste dispositivo.",
    "error_insecure": "A prova exige uma página segura (https).",
    "error_in_use": "A câmera está sendo usada por outro aplicativo. Feche-o e tente novamente.",
    "error_unsupported": "Seu navegador não é compatível com a prova. Tente Safari (iOS) ou Chrome (Android).",
    "error_engine": "Não foi possível iniciar o sistema de prova.",
    "error_graphics": "Seu navegador está com pouca memória gráfica. Feche outras abas ou atualize a página.",
    "retry": "Tentar novamente",
    "close": "Fechar",
    "pd_label": "Distância pupilar estimada",
    "pd_note": "Apenas indicativa — não é uma medida médica.",
    "size_suggestion": "Tamanho sugerido"
  },
  "ru": {
    "consent_title": "Виртуальная примерка",
    "consent_body": "Камера используется только на этом устройстве, чтобы показать оправу на вашем лице. Во время примерки изображения не отправляются. Для отправки данных заказа требуется ваше согласие.",
    "consent_allow": "Разрешить камеру",
    "consent_cancel": "Отмена",
    "handmade_title": "Размер и цвета",
    "material_preview": "Просмотр материала",
    "sizing_help": "Как это работает?",
    "sizing_title": "Настроить размер",
    "sizing_pd": "Общее межзрачковое расстояние для дали (мм)",
    "sizing_pd_short": "Межзрачковое расстояние",
    "sizing_optional": "Необязательно",
    "sizing_pd_auto": "Автоматическая оценка межзрачкового расстояния",
    "sizing_apply": "Применить",
    "sizing_reset": "Сбросить",
    "sizing_pd_note": "Введите известное вам межзрачковое расстояние для дали. Если у вас два значения, сложите правое и левое. Оставьте поле пустым для автоматической настройки.",
    "sizing_auto": "Автоматическая настройка",
    "sizing_handmade": "Ширина оправы",
    "sizing_handmade_note": "Размеры от XS до XL соответствуют полной ширине передней части оправы. Межзрачковое расстояние используется для настройки масштаба изображения на вашем лице. Пропорции сохраняются; окончательные размеры нужно подтвердить с мастерской.",
    "sizing_calibrating": "Посмотрите прямо в камеру, чтобы настроить масштаб изображения.",
    "sizing_calibrated": "Масштаб настроен по вашему межзрачковому расстоянию:",
    "loading": "Загрузка системы примерки…",
    "hint_no_face": "Расположите лицо в рамке",
    "hint_too_dark": "Найдите более светлое место",
    "hint_too_far": "Подойдите ближе",
    "hint_too_close": "Немного отодвиньтесь",
    "hint_look_at_camera": "Посмотрите в камеру",
    "hint_multiple_faces": "Только одно лицо в кадре",
    "hint_hold_still": "Не двигайтесь",
    "error_denied": "Доступ к камере отклонён. Разрешите использование камеры в настройках браузера, чтобы примерять оправы.",
    "error_no_device": "На этом устройстве не найдена камера.",
    "error_insecure": "Для примерки нужна защищённая страница (https).",
    "error_in_use": "Камеру использует другое приложение. Закройте его и попробуйте снова.",
    "error_unsupported": "Ваш браузер не поддерживает примерку. Попробуйте Safari (iOS) или Chrome (Android).",
    "error_engine": "Не удалось запустить систему примерки.",
    "error_graphics": "Браузеру не хватает графической памяти. Закройте другие вкладки или обновите страницу.",
    "retry": "Повторить",
    "close": "Закрыть",
    "pd_label": "Оценка межзрачкового расстояния",
    "pd_note": "Только для ориентира — это не медицинское измерение.",
    "size_suggestion": "Предлагаемый размер"
  },
  "zh-CN": {
    "consent_title": "虚拟试戴",
    "consent_body": "摄像头仅在此设备上使用，用来将镜框叠加到您的脸上。试戴时不会发送任何图像。发送订单资料需要获得您的同意。",
    "consent_allow": "允许使用摄像头",
    "consent_cancel": "取消",
    "handmade_title": "尺寸与颜色",
    "material_preview": "材料预览",
    "sizing_help": "如何使用？",
    "sizing_title": "调整尺寸",
    "sizing_pd": "远用双眼总瞳距（mm）",
    "sizing_pd_short": "瞳距",
    "sizing_optional": "可选",
    "sizing_pd_auto": "自动估算瞳距",
    "sizing_apply": "应用",
    "sizing_reset": "重置",
    "sizing_pd_note": "请输入您已知的远用瞳距。如果有左右眼两个数值，请相加。留空则自动调整。",
    "sizing_auto": "自动调整",
    "sizing_handmade": "镜框宽度",
    "sizing_handmade_note": "XS 至 XL 尺寸对应镜框前部的总宽度。瞳距用于调整脸上预览图的比例。形状比例保持不变，最终尺寸仍需与工坊确认。",
    "sizing_calibrating": "请正对摄像头，以调整预览比例。",
    "sizing_calibrated": "已根据您的瞳距调整比例：",
    "loading": "正在加载试戴引擎…",
    "hint_no_face": "请将脸部放入取景框内",
    "hint_too_dark": "请移至光线更充足处",
    "hint_too_far": "请靠近一些",
    "hint_too_close": "请稍微后退",
    "hint_look_at_camera": "请看向摄像头",
    "hint_multiple_faces": "每次只让一张脸进入画面",
    "hint_hold_still": "请保持不动",
    "error_denied": "摄像头访问被拒绝。请在浏览器设置中允许使用摄像头，以试戴镜框。",
    "error_no_device": "此设备未找到摄像头。",
    "error_insecure": "试戴需要在安全页面（https）中使用。",
    "error_in_use": "摄像头正被其他应用使用。请关闭该应用后重试。",
    "error_unsupported": "您的浏览器不支持试戴。请尝试 Safari（iOS）或 Chrome（Android）。",
    "error_engine": "试戴引擎无法启动。",
    "error_graphics": "浏览器图形内存不足。请关闭其他标签页或刷新页面。",
    "retry": "重试",
    "close": "关闭",
    "pd_label": "估算瞳距",
    "pd_note": "仅供参考，并非医学测量。",
    "size_suggestion": "建议尺寸"
  }
} as const;

export type StringKey = keyof (typeof STRINGS)['en'];

let current: Lang = 'en';

export function detectLang(explicit?: string | null): Lang {
  const candidates = [explicit, new URLSearchParams(location.search).get('lang'), document.documentElement.lang, navigator.language];
  for (const c of candidates) {
    if (!c) continue;
    const code = c.toLowerCase().split('-')[0] || ''; 
    if (code === 'pt') return 'pt-BR';
    if (code === 'zh') return 'zh-CN';
    if (['en','fr','id','de','es','it','nl','ru','ja','ko','ar','hi','ms'].includes(code)) return code as Lang;
    if (code === 'in') return 'id'; // legacy Indonesian code
  }
  return 'en';
}

export function setLang(lang: Lang): void {
  current = lang;
}

export function getLang(): Lang {
  return current;
}

export function t(key: StringKey): string {
  return ALL_STRINGS[current][key];
}

const ALL_STRINGS: Record<Lang, Record<StringKey, string>> = STRINGS;
